import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

// The directory changes only when HealthMail's master list is reloaded, and a GP list is
// thousands of rows, so a picker opened twice in a session should not fetch it twice.
const DIRECTORY_STALE_TIME_MS = 30 * 60 * 1000;

/**
 * @param {string[]} titles HealthMail contact titles to list, e.g. ["Pharmacy"]. Required:
 * the directory holds about 11,200 contacts of every kind (KIN-2211), so nothing may fetch
 * it unfiltered. An empty `title` is a 400 as well.
 * @param {{ enabled?: boolean }} [options] `enabled: false` defers the fetch, e.g. until
 * a picker is opened.
 */
export const useHealthMailContactsList = (titles, { enabled = true } = {}) => {
  const tenant =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const hasTitles =
    Array.isArray(titles) &&
    titles.length > 0 &&
    titles.every((title) => typeof title === "string" && title.trim() !== "");

  if (!hasTitles) {
    console.error(
      "useHealthMailContactsList needs a non-empty list of titles; the unfiltered directory is not fetched.",
      titles
    );
  }

  const fetchHealthMailContactsList = async () => {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.get(`${tenant}/healthmailcontacts`, {
      params: { title: titles.join(",") },
    });

    return response.data;
  };

  return useQuery({
    queryKey: ["healthMailContactsList", tenant, titles],
    queryFn: fetchHealthMailContactsList,
    enabled: enabled && !!tenant && hasTitles,
    staleTime: DIRECTORY_STALE_TIME_MS,
    meta: { errorMessage: "Error loading HealthMail contacts" },
  });
};

export default useHealthMailContactsList;
